import { useCallback, useState } from 'react';

import { type TreeSpecies } from '../../../api/settings';
import {
  createWorkshopType,
  updateWorkshopType,
  WorkshopTypeError,
  type WorkshopType,
  type WorkshopTypeInput,
} from '../../../api/workshop-types';

/** "65" / "64.50" dollar string → integer cents, or null when blank/invalid. */
export function dollarsToCents(input: string): number | null {
  const cleaned = input.replace(/[$,\s]/g, '');
  if (cleaned === '') return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
}

/** Convert a duration value + unit into integer minutes, or null when blank. */
export function durationToMinutes(value: string, unit: 'hours' | 'minutes'): number | null {
  if (!value) return null;
  return unit === 'hours' ? Math.round(parseFloat(value) * 60) : parseInt(value);
}

/** Seed the duration unit + text value from a type's stored minutes. */
export function seedDuration(minutes: number | null | undefined): {
  unit: 'hours' | 'minutes';
  value: string;
} {
  if (minutes === null || minutes === undefined) return { unit: 'minutes', value: '' };
  return minutes % 60 === 0
    ? { unit: 'hours', value: String(minutes / 60) }
    : { unit: 'minutes', value: String(minutes) };
}

/** Seed the dollar price text field from a type's stored cents. */
export function seedPrice(cents: number | null | undefined): string {
  return cents !== null && cents !== undefined ? (cents / 100).toFixed(2) : '';
}

export interface WorkshopTypeFormState {
  name: string;
  displayName: string;
  treeSpeciesId: string;
  description: string;
  defaultPrice: string;
  defaultCapacity: string;
  durationValue: string;
  durationUnit: 'hours' | 'minutes';
  coveredByTreeGuarantee: boolean;
  isActive: boolean;
}

export function buildPayload(form: WorkshopTypeFormState): WorkshopTypeInput {
  return {
    name: form.name.trim(),
    displayName: form.displayName.trim() || null,
    treeSpeciesId: form.treeSpeciesId || null,
    description: form.description.trim() || null,
    defaultPriceCents: dollarsToCents(form.defaultPrice),
    defaultCapacity: form.defaultCapacity ? parseInt(form.defaultCapacity) : null,
    defaultDurationMinutes: durationToMinutes(form.durationValue, form.durationUnit),
    coveredByTreeGuarantee: form.coveredByTreeGuarantee,
    isActive: form.isActive,
  };
}

/** Extract the first field-error message from a thrown error, else its message. */
export function workshopTypeErrorMessage(err: unknown, fallback: string): string {
  const fieldMsg =
    err instanceof WorkshopTypeError && err.fieldErrors
      ? Object.values(err.fieldErrors)[0]
      : undefined;
  return fieldMsg ?? (err instanceof Error ? err.message : fallback);
}

export interface FormSetters {
  setName: (v: string) => void;
  setDisplayName: (v: string) => void;
  setTreeSpeciesId: (v: string) => void;
  setDescription: (v: string) => void;
  setDefaultPrice: (v: string) => void;
  setDefaultCapacity: (v: string) => void;
  setDurationValue: (v: string) => void;
  setDurationUnit: (v: 'hours' | 'minutes') => void;
  setCoveredByTreeGuarantee: (v: boolean) => void;
  setIsActive: (v: boolean) => void;
}

/** Field-component prop bag: current values + their setters. */
export interface FieldsProps extends FormSetters {
  form: WorkshopTypeFormState;
  treeSpeciesList: TreeSpecies[];
}

/** Seed form state from a loaded workshop type, mirroring the ops DTO shape. */
export function seedFormFromType(type: WorkshopType, s: FormSetters): void {
  s.setName(type.name);
  s.setDisplayName(type.displayName ?? '');
  s.setTreeSpeciesId(type.treeSpeciesId ?? '');
  const minutes = type.defaultDurationMinutes ?? null;
  if (minutes !== null) {
    const duration = seedDuration(minutes);
    s.setDurationUnit(duration.unit);
    s.setDurationValue(duration.value);
  }
  s.setDefaultPrice(seedPrice(type.defaultPriceCents));
  s.setDefaultCapacity(type.defaultCapacity !== null ? String(type.defaultCapacity) : '');
  s.setDescription(type.description || '');
  s.setCoveredByTreeGuarantee(type.coveredByTreeGuarantee);
  s.setIsActive(type.isActive);
}

/** Create or update a workshop type from the form state. */
export function persistWorkshopType(
  existing: WorkshopType | null,
  form: WorkshopTypeFormState,
): Promise<WorkshopType> {
  const payload = buildPayload(form);
  return existing ? updateWorkshopType(existing.id, payload) : createWorkshopType(payload);
}

export interface WorkshopTypeFormApi {
  form: WorkshopTypeFormState;
  treeSpeciesList: TreeSpecies[];
  setTreeSpeciesList: (list: TreeSpecies[]) => void;
  /** Stable seed callback — applies a loaded type to the field state. */
  seed: (type: WorkshopType) => void;
  /** Assemble the prop bag consumed by <WorkshopTypeFields />. */
  fields: FieldsProps;
}

/**
 * Owns all workshop-type form field state. `initial` seeds the fields for the
 * edit case; pass null/undefined for the create case or when the record loads
 * asynchronously (use the returned `seed` callback).
 */
export function useWorkshopTypeForm(initial?: WorkshopType | null): WorkshopTypeFormApi {
  const initDuration = seedDuration(initial?.defaultDurationMinutes);
  const [name, setName] = useState(initial?.name || '');
  const [displayName, setDisplayName] = useState(initial?.displayName || '');
  const [treeSpeciesId, setTreeSpeciesId] = useState(initial?.treeSpeciesId || '');
  const [treeSpeciesList, setTreeSpeciesList] = useState<TreeSpecies[]>([]);
  const [durationUnit, setDurationUnit] = useState<'hours' | 'minutes'>(initDuration.unit);
  const [durationValue, setDurationValue] = useState(initDuration.value);
  const [defaultPrice, setDefaultPrice] = useState(seedPrice(initial?.defaultPriceCents));
  const [defaultCapacity, setDefaultCapacity] = useState(
    initial?.defaultCapacity?.toString() || '',
  );
  const [description, setDescription] = useState(initial?.description || '');
  const [coveredByTreeGuarantee, setCoveredByTreeGuarantee] = useState(
    initial?.coveredByTreeGuarantee ?? false,
  );
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  const form: WorkshopTypeFormState = {
    name,
    displayName,
    treeSpeciesId,
    description,
    defaultPrice,
    defaultCapacity,
    durationValue,
    durationUnit,
    coveredByTreeGuarantee,
    isActive,
  };

  const setters: FormSetters = {
    setName,
    setDisplayName,
    setTreeSpeciesId,
    setDescription,
    setDefaultPrice,
    setDefaultCapacity,
    setDurationValue,
    setDurationUnit,
    setCoveredByTreeGuarantee,
    setIsActive,
  };

  // Stable across renders — every setter dependency is a stable useState
  // dispatcher, so the empty dep list is correct.
  const seed = useCallback((type: WorkshopType) => {
    seedFormFromType(type, {
      setName,
      setDisplayName,
      setTreeSpeciesId,
      setDescription,
      setDefaultPrice,
      setDefaultCapacity,
      setDurationValue,
      setDurationUnit,
      setCoveredByTreeGuarantee,
      setIsActive,
    });
  }, []);

  return {
    form,
    treeSpeciesList,
    setTreeSpeciesList,
    seed,
    fields: { form, treeSpeciesList, ...setters },
  };
}
